import axios from 'axios';
import React from 'react';
import { IUser, UserApi } from '../api/UserApi';
import AppBarContext from '../context/AppBarContext';
import { isJwtLoggedIn } from '../util/session';

export function landingForRole(role: IUser['role']): string {
    return role === 'teacher' || role === 'admin' ? '/teacher' : '/student';
}

export interface SignedInAccount {
    email: string | null;
    landing: string;
}

export interface SignedInAccountState {
    account: SignedInAccount | null;
    /** The session looked valid but the account could not be loaded (network or server error). */
    unavailable: boolean;
    retry: () => void;
}

// Resolves the signed-in account for public pages. `account` is null while signed out, while
// loading, or when the session can no longer be read. A rejected session (401) is cleared like a
// logout; any other failure sets `unavailable` so the page can offer a retry.
export function useSignedInAccountState(onUnauthorized?: () => void): SignedInAccountState {
    const { setIsLoggedIn } = React.useContext(AppBarContext);
    const [account, setAccount] = React.useState<SignedInAccount | null>(null);
    const [unavailable, setUnavailable] = React.useState(false);
    const [attempt, setAttempt] = React.useState(0);
    React.useEffect(() => {
        if (!isJwtLoggedIn()) return;
        let active = true;
        UserApi.get()
            .then(response => {
                if (!active) return;
                setUnavailable(false);
                setAccount({ email: response.data.email ?? localStorage.getItem('userEmail'), landing: landingForRole(response.data.role) });
            })
            .catch(error => {
                if (!active) return;
                if (!axios.isAxiosError(error) || error.response?.status !== 401) {
                    setUnavailable(true);
                    return;
                }
                // Match logging out, so a rejected session leaves no account details behind.
                localStorage.clear();
                setIsLoggedIn(false);
                onUnauthorized?.();
            });
        return () => { active = false; };
    }, [onUnauthorized, setIsLoggedIn, attempt]);
    const retry = React.useCallback(() => {
        setUnavailable(false);
        setAttempt(value => value + 1);
    }, []);
    return { account, unavailable, retry };
}

export function useSignedInAccount(onUnauthorized?: () => void): SignedInAccount | null {
    return useSignedInAccountState(onUnauthorized).account;
}
